import {
  Calendar,
  CheckCircle,
  ChevronDown,
  Clock,
  XCircle
} from "lucide-react";
import React from "react";
import { EDSButton } from "../../../../eds/EDSButton";
import { EDSSearch } from "../../../../eds/EDSSearch";
import { Button } from "../../../../ui/button";
import { WebViewPopover } from "../../SubHeader/WebViewPopover";
import { BookingStats } from "../hooks/useBookingStats";
// FilterType is now just string - no import needed
import { VenueGroupConfig } from "../../../../../types/VenueGroup";
import { VenueConfig } from "../../../../../types/venues";
import {
  getBookingStages,
  getExcludedStages
} from "../../../../../utils/booking";

interface RunsheetToolbarProps {
  searchQuery: string;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClearSearch: () => void;
  activeFilter: string;
  onFilterChange: (filter: string) => void;
  isFilterPopoverOpen: boolean;
  onFilterPopoverChange: (open: boolean) => void;
  sortOrder: string;
  onSortToggle: () => void;
  stats: BookingStats;
  venueConfig: VenueConfig | null;
  venueGroupConfig: VenueGroupConfig | null;
}

export const RunsheetToolbar: React.FC<RunsheetToolbarProps> = ({
  searchQuery,
  onSearchChange,
  onClearSearch,
  activeFilter,
  onFilterChange,
  isFilterPopoverOpen,
  onFilterPopoverChange,
  sortOrder,
  onSortToggle,
  stats,
  venueConfig,
  venueGroupConfig
}) => {
  // Generate dynamic filter options based on venue configuration
  const stages =
    venueConfig && venueGroupConfig
      ? getBookingStages({ venueConfig, venueGroupConfig })
      : [];

  // Define excluded stages that should not appear in filter dropdown
  const excludedStages = getExcludedStages();

  const filterOptions = [
    { key: "all", label: "All bookings" },
    { key: "awaiting-arrival", label: "Awaiting Arrival" },
    { key: "checked-in", label: "Checked In" },
    // Add dynamic stage filters (excluding terminal stages)
    ...stages
      .filter((stage) => !excludedStages.includes(stage.id.toLowerCase()))
      .map((stage) => ({
        key: stage.id,
        label: stage.label
      }))
  ];

  const getFilterDisplayLabel = (filter: string): string => {
    switch (filter) {
      case "all":
        return "All";
      case "awaiting-arrival":
        return "Awaiting Arrival";
      case "checked-in":
        return "Checked In";
      default: {
        // For dynamic stage filters, find the stage label
        const stage = stages.find((s) => s.id === filter);
        return stage?.label ?? filter;
      }
    }
  };

  const getSortButtonText = () => {
    switch (sortOrder) {
      case "asc":
        return "Sort A - Z";
      case "desc":
        return "Sort Z - A";
      default:
        return "Sort";
    }
  };

  return (
    <div className="RunSheet_Beta_Toolbar w-full bg-card  py-2">
      <div className="RunSheet_Beta_ToolbarRow flex items-center gap-2 mb-3 h-auto justify-between">
        {/* Left Side Controls */}
        <div className="RunSheet_Beta_LeftControls flex items-center gap-2 flex-1">
          {/* Search - Using EDS Component */}
          <div className="RunSheet_Beta_SearchInline relative flex-1 max-w-[300px]">
            <EDSSearch
              id="runsheet-search-input"
              name="runsheet-search"
              placeholder="Search..."
              value={searchQuery}
              onChange={onSearchChange}
              className="w-full"
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onClearSearch}
                className="absolute right-0 top-1/2 transform -translate-y-1/2 h-7 w-7"
              >
                <XCircle className="h-3.5 w-3.5" />
              </Button>
            )}
          </div>

          {/* Filter Dropdown */}
          <div className="RunSheet_Beta_FilterDropdown shrink-0">
            <WebViewPopover
              open={isFilterPopoverOpen}
              onOpenChange={onFilterPopoverChange}
              side="bottom"
              sideOffset={8}
              content={
                <div className="max-h-[300px] overflow-y-auto px-2 pt-2 pb-2">
                  {filterOptions.map((filter) => {
                    const isSelected = filter.key === activeFilter;
                    return (
                      <button
                        key={filter.key}
                        className={`w-full px-3 py-2 text-left text-sm focus:outline-none rounded-md ${
                          isSelected
                            ? "bg-accent text-accent-foreground font-medium"
                            : "hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                        }`}
                        onClick={() => {
                          onFilterChange(filter.key);
                          onFilterPopoverChange(false);
                        }}
                      >
                        {filter.label}
                      </button>
                    );
                  })}
                </div>
              }
            >
              <button className="filter-trigger flex items-center justify-between min-w-[140px] py-2 px-3 text-sm bg-background border border-border rounded-md text-foreground cursor-pointer whitespace-nowrap hover:bg-accent hover:text-accent-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-ring">
                Show: {getFilterDisplayLabel(activeFilter)}
                <ChevronDown className="h-4 w-4 ml-2" />
              </button>
            </WebViewPopover>
          </div>

          <div className="RunSheet_Beta_Sort shrink-0">
            <EDSButton
              variant={sortOrder !== "none" ? "default" : "secondary"}
              onClick={onSortToggle}
            >
              {getSortButtonText()}
            </EDSButton>
          </div>
        </div>

        {/* Compact Stats Group - Far Right */}
        <div className="RunSheet_Beta_StatsGroup hidden md:flex items-center gap-3 shrink-0 bg-background border border-border rounded-full shadow-md px-4 py-2">
          <div className="stat-item stat-total">
            <Calendar className="h-4 w-4" />
            <span className="stat-value">{stats.total}</span>
          </div>
          <div className="stat-item stat-checked">
            <CheckCircle className="h-4 w-4" />
            <span className="stat-value">{stats.checkedIn}</span>
          </div>
          <div className="stat-item stat-pending">
            <Clock className="h-4 w-4" />
            <span className="stat-value">{stats.pending}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
